// src/components/Footer.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Facebook, Twitter, Instagram, Mail, MapPin, Phone, ChevronUp } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Mail, href: 'mailto:themaseiduedufund@gmail.com', label: 'Email' },
  ];

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Board', path: '/board' },
    { name: 'Contact', path: '/contact' },
  ];

  const contactInfo = [
    { icon: MapPin, text: 'Bomandi International School c/o P. O. Box 181 Tiegberi, Wa Upper West Region' },
    { icon: Mail, text: 'themaseiduedufund@gmail.com' },
    { icon: Phone, text: '+233 55 160 5410 / +233 20 268 3143 / +233 20 878 5404' },
  ];

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8 }}
      className="relative bg-gradient-to-b from-gray-900 via-gray-950 to-black text-white overflow-hidden"
    >
      {/* Subtle background glow */}
      <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 via-transparent to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">

          {/* Logo & Description */}
          <div className="lg:col-span-2 space-y-8">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="inline-block"
            >
              <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                M.A Seidu Education Fund
              </h2>
            </motion.div>

            <p className="text-gray-300 text-base lg:text-lg leading-relaxed max-w-xl">
              Empowering futures through educational funding. We believe every student deserves access to quality education regardless of their financial background.
            </p>

            {/* Social Links */}
            <div className="flex gap-5">
              {socialLinks.map((social, i) => (
                <motion.a
                  key={i}
                  href={social.href}
                  target={social.href.startsWith('mailto') ? undefined : '_blank'}
                  rel="noopener noreferrer"
                  whileHover={{ y: -4, scale: 1.2 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-blue-500/20 hover:border-blue-400 transition-all duration-300 group"
                  aria-label={social.label}
                >
                  <social.icon size={20} className="text-gray-300 group-hover:text-blue-400 transition-colors" />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold text-white mb-6 relative inline-block after:content-[''] after:absolute after:-bottom-2 after:left-0 after:w-12 after:h-1 after:bg-blue-500 after:rounded-full">
              Quick Links
            </h3>
            <ul className="space-y-4 mt-8">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-blue-400 transition-all duration-300 flex items-center group text-base lg:text-lg"
                  >
                    <span className="w-0 group-hover:w-6 transition-all duration-300 mr-2">→</span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold text-white mb-6 relative inline-block after:content-[''] after:absolute after:-bottom-2 after:left-0 after:w-12 after:h-1 after:bg-blue-500 after:rounded-full">
              Contact Us
            </h3>
            <div className="space-y-5 mt-8">
              {contactInfo.map((item, i) => (
                <motion.div
                  key={i}
                  whileHover={{ x: 6 }}
                  className="flex items-start gap-4 text-gray-300 group"
                >
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/20 transition-all">
                    <item.icon size={18} className="text-blue-400" />
                  </div>
                  <span className="text-base lg:text-lg leading-relaxed">{item.text}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-10 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 text-center md:text-left">
            <p className="text-gray-400 text-sm lg:text-base">
              © {currentYear} <span className="text-blue-400 font-medium">M.A Seidu Education Fund</span>. All rights reserved.
            </p>
            <p className="text-gray-500 text-xs lg:text-sm italic">
              Empowering Education • Transforming Lives • Building Tomorrow
            </p>
          </div>
        </div>

        {/* Back to Top Button */}
        <motion.button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="fixed bottom-8 right-8 z-50 w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 shadow-2xl flex items-center justify-center border-4 border-white/20 backdrop-blur-sm transition-all duration-300"
          aria-label="Back to top"
        >
          <ChevronUp size={28} className="text-white" />
        </motion.button>
      </div>
    </motion.footer>
  );
};

export default Footer;